import CachedIcon from "@mui/icons-material/Cached";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchOff from "@mui/icons-material/SearchOff";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { useState } from "react";
import { useTranslationHook } from "../../hooks";
import { Project } from "../../types/model/project";

const ProjectRow = ({ project, onRowClick }: { project: Project; onRowClick: (p: Project) => void }) => {
  const { t } = useTranslationHook();

  const formatDate = (date?: Date | string) => {
    if (!date) return "";

    const parsed = new Date(date);

    return Number.isNaN(parsed.getTime())
      ? ""
      : new Intl.DateTimeFormat("pt-PT").format(parsed);
  };

  const tooltipContent = (
    <Box sx={{ p: 1, maxWidth: 260 }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {t("admin.projects.description")}
      </Typography>
      <Typography variant="body2" color="text.primary">
        {project.description || t("generic.no-description")}
      </Typography>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            width: 280,
            maxWidth: 280,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 3,
            "& .MuiTooltip-arrow": {
              color: "background.paper",
              "&::before": {
                border: "1px solid",
                borderColor: "divider",
              },
            },
          },
        },
      }}
    >
      <TableRow
        hover
        onClick={() => onRowClick(project)}
        sx={{ cursor: "pointer" }}
      >
        <TableCell><Typography>{project.name}</Typography></TableCell>
        <TableCell>{formatDate(project.startDate)}</TableCell>
        <TableCell>{formatDate(project.endDate)}</TableCell>
      </TableRow>
    </Tooltip>
  );
};

interface Props {
  tableData: Project[];
  onRequestRefresh: () => void;
  onRowClick: (project: Project) => void;
}

const ProjectTable = ({
  tableData,
  onRequestRefresh,
  onRowClick,
}: Props) => {
  const { t } = useTranslationHook();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");

  const filteredData = tableData.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const hasData = visibleRows.length > 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("admin.accounts.account-projects")}
          </Typography>

          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {showFilters && (
          <Grid2 container spacing={3} sx={{ mb: 2 }}>
            <Grid2 size={12}>
              <TextField
                label={t("admin.projects.search-placeholder")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                fullWidth
              />
            </Grid2>
          </Grid2>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.projects.project-name")}</TableCell>
                <TableCell>{t("admin.projects.start-date")}</TableCell>
                <TableCell>{t("admin.projects.end-date")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {hasData ? (
                visibleRows.map((project) => (
                  <ProjectRow key={project.id} project={project} onRowClick={onRowClick} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography>
                        {t("projects.no-projects")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default ProjectTable;